import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { bulkApi, type BulkOperationResult } from '@/api/bulk'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import { BulkResultSummary } from './BulkResultSummary'

interface CardBulkDeleteDialogProps {
  open: boolean
  cardIds: number[]
  onClose: () => void
  /** Вызывается после успешной операции — страница перезагружает список и снимает выделение. */
  onFinished: () => void
}

/**
 * Массовое удаление выбранных карточек.
 *
 * Других массовых действий над карточками нет: теги живут на слове, а подборки
 * собираются в словаре учителя — см. `.cursor/rules/easya-tags-belong-to-words.mdc`.
 */
export function CardBulkDeleteDialog({
  open,
  cardIds,
  onClose,
  onFinished,
}: CardBulkDeleteDialogProps) {
  const { t } = useTranslation()

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<BulkOperationResult | null>(null)

  const handleClose = () => {
    setError(null)
    setResult(null)
    onClose()
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      setResult(await bulkApi.deleteCards(cardIds))
      onFinished()
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('cards.bulk.actionError')))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('cards.bulk.deleteTitle')}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {result ? (
          <BulkResultSummary result={result} />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
            {t('cards.bulk.deleteHint', { count: cardIds.length })}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {result ? t('common.close', 'Закрыть') : t('common.cancel')}
        </Button>
        {!result && (
          <Button
            variant="contained"
            color="error"
            disabled={submitting}
            onClick={() => void handleSubmit()}
          >
            {submitting ? t('common.saving', 'Сохранение...') : t('cards.bulk.delete')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
